/* EPIC quantity bundles enhance native Shopify product forms; no cart API writes. */
if (!customElements.get('epic-bundle-selector')) {
  customElements.define('epic-bundle-selector', class EpicBundleSelector extends HTMLElement {
    static limits(state) {
      const inCart = Math.max(0, Number(state.cartVariantQuantity) || 0);
      const step = Math.max(1, Number(state.increment) || 1);
      const ruleMinimum = Math.max(1, Number(state.minimum) || 1);
      const min = inCart >= ruleMinimum ? Math.min(ruleMinimum, step) : ruleMinimum;
      let max = state.maximum == null ? Infinity : Number(state.maximum) - inCart;
      if (state.inventoryManaged && state.inventoryPolicy === 'deny') {
        max = Math.min(max, Number(state.inventoryQuantity) - inCart);
      }
      return { min, max: Math.max(0, max), step };
    }

    static canSelect(quantity, state) {
      const { min, max, step } = this.limits(state);
      return Boolean(state.available) && quantity >= min && quantity <= max && (quantity - min) % step === 0;
    }

    static offer(quantity, state) {
      // Each option advertises its own quantity tier, independent of the existing cart.
      const configured = quantity >= 3 ? state.discountPerItem3 : quantity >= 2 ? state.discountPerItem2 : 0;
      const saving = state.discountsEnabled ? Math.min(Number(state.price), Math.max(0, Number(configured) || 0)) : 0;
      const total = (Number(state.price) - saving) * quantity;
      return { saving, total, each: Math.round(total / quantity) };
    }

    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.quantityInput = this.productInfo?.querySelector('.quantity__input');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.productInfo || !this.quantityInput || !this.form) return;
      this.state = this.readState(this);
      if (!this.state) return; // Server-rendered, form-owned radios remain usable.
      this.selectedQuantity = Number(this.querySelector('input:checked')?.value) || 1;
      this.onChange = (event) => {
        if (!event.target.matches('.epic-bundle__radio')) return;
        this.selectedQuantity = Number(event.target.value);
        this.render(true);
      };
      this.onVariantIdChange = (event) => {
        if (event.target.matches('input[name="id"]') && !event.target.value) {
          this.pending = true;
          this.render();
        }
      };
      this.onSubmit = (event) => {
        if (this.pending || !this.constructor.canSelect(this.selectedQuantity, this.state)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.status.textContent = 'Choose an available quantity before adding to cart.';
        }
      };
      this.status = this.querySelector('[data-bundle-status]');
      this.addEventListener('change', this.onChange);
      this.productInfo.addEventListener('change', this.onVariantIdChange);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (!this.productInfo.contains(data.event.target)) return;
          this.pending = true;
          this.render();
        }));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          const source = data.html.querySelector('epic-bundle-selector');
          const nextState = source && this.readState(source);
          if (!nextState) return;
          this.state = nextState;
          this.pending = false;
          this.render(true);
        }));
        // Dawn resets its internal input to the minimum during rule updates.
        // Restore the customer's chosen radio quantity after those updates finish.
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          queueMicrotask(() => { if (this.isConnected && !this.pending) this.render(); });
        }));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartQuantities()));
      }
      this.ruleObserver = new MutationObserver(() => {
        this.state.cartVariantQuantity = Number(this.quantityInput.dataset.cartQuantity) || 0;
        this.state.minimum = Number(this.quantityInput.dataset.min) || 1;
        this.state.increment = Number(this.quantityInput.step) || 1;
        this.state.maximum = this.quantityInput.hasAttribute('data-max') ? Number(this.quantityInput.dataset.max) : null;
        if (!this.pending) this.render();
      });
      this.ruleObserver.observe(this.quantityInput, { attributes: true, attributeFilter: ['data-cart-quantity', 'data-min', 'data-max', 'step'] });
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.productInfo?.removeEventListener('change', this.onVariantIdChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      this.ruleObserver?.disconnect();
      this.cartController?.abort();
    }

    readState(element) {
      try { return JSON.parse(element.querySelector('[data-epic-bundle-state]').textContent); }
      catch { return null; }
    }

    money(cents) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: this.state.currency || 'INR', minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2,
      }).format(cents / 100);
    }

    render(announce = false) {
      const quantities = [1, 2, 3].filter((quantity) => this.constructor.canSelect(quantity, this.state));
      const previousQuantity = this.selectedQuantity;
      if (!quantities.includes(this.selectedQuantity)) this.selectedQuantity = quantities[0] || 0;
      this.querySelectorAll('[data-bundle-choice]').forEach((card) => {
        const quantity = Number(card.dataset.bundleChoice);
        const radio = card.querySelector('input');
        const eligible = quantities.includes(quantity);
        const offer = this.constructor.offer(quantity, this.state);
        radio.disabled = Boolean(this.pending) || !eligible;
        radio.checked = quantity === this.selectedQuantity;
        card.querySelector('[data-bundle-total]').textContent = this.money(offer.total);
        const originalTotal = Number(this.state.price) * quantity;
        const original = card.querySelector('[data-bundle-original]');
        original.hidden = quantity === 1 || originalTotal <= offer.total;
        original.querySelector('[data-bundle-original-value]').textContent = this.money(originalTotal);
        card.querySelector('[data-bundle-each]').textContent = this.money(offer.each);
        const daily = card.querySelector('[data-bundle-daily]');
        if (daily) daily.textContent = this.money(Math.round(offer.each / 365));
        const savings = card.querySelector('[data-bundle-saving]');
        savings.hidden = !offer.saving;
        savings.textContent = `Save ${this.money(this.state.price * quantity - offer.total)}`;
        card.querySelector('[data-bundle-unavailable]').hidden = eligible;
      });
      this.setAttribute('aria-busy', this.pending ? 'true' : 'false');
      if (this.pending) return;
      const { min, max, step } = this.constructor.limits(this.state);
      this.quantityInput.min = min;
      // Avoid re-triggering our attribute observer when the rule has not changed.
      if (Number(this.quantityInput.step) !== step) this.quantityInput.step = step;
      if (Number.isFinite(max)) this.quantityInput.max = max;
      else this.quantityInput.removeAttribute('max');
      this.quantityInput.value = this.selectedQuantity || min;
      const productForm = this.productInfo.querySelector('product-form');
      if (!quantities.length) {
        productForm?.toggleSubmitButton?.(true);
        this.blockedByQuantity = true;
        this.status.textContent = 'This selection cannot be added in the available quantities. Check the quantity already in your cart.';
      } else {
        if (this.blockedByQuantity) productForm?.toggleSubmitButton?.(false);
        this.blockedByQuantity = false;
        if (announce) {
          const offer = this.constructor.offer(this.selectedQuantity, this.state);
          const adjustment = previousQuantity !== this.selectedQuantity ? 'Quantity adjusted for available stock. ' : '';
          this.status.textContent = `${adjustment}${this.selectedQuantity} ${this.selectedQuantity === 1 ? 'calendar' : 'calendars'}, ${this.money(offer.total)} total.`;
        }
      }
    }

    async refreshCartQuantities() {
      this.cartController?.abort();
      this.cartController = new AbortController();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin', signal: this.cartController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Cart quantities could not be refreshed');
        const cart = await response.json();
        this.state.cartProductQuantity = cart.items.filter((item) => String(item.product_id) === this.dataset.productId).reduce((sum, item) => sum + item.quantity, 0);
        this.state.cartVariantQuantity = cart.items.filter((item) => String(item.variant_id) === String(this.state.variantId)).reduce((sum, item) => sum + item.quantity, 0);
        this.quantityInput.dataset.cartQuantity = this.state.cartVariantQuantity;
        if (!this.pending) this.render();
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('EPIC bundle: cart quantity refresh unavailable.', error);
      }
    }
  });
}
